import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight, Compass, Building2, Phone } from "lucide-react";
import { Reveal, Overline } from "@/components/public/Reveal";
import ZoneMap from "@/components/public/ZoneMap";
import CallbackForm from "@/components/public/CallbackForm";
import Seo, { localBusinessLd } from "@/components/Seo";
import api, { fileUrl } from "@/lib/api";

const ART_FALLBACK = "https://images.unsplash.com/photo-1558904541-efa843a96f01?w=1200&q=80";

export default function ZoneIntervention() {
  const [zone, setZone] = useState(null);
  useEffect(() => { api.get("/zone").then((r) => setZone(r.data)).catch(() => {}); }, []);

  const pages = zone?.pages || [];
  const cities = zone?.cities || [];
  const radius = zone?.radius_km || 60;

  return (
    <div className="bg-cream">
      <Seo title="Zone d'intervention — Paysagiste Bouches-du-Rhône, Var & Vaucluse"
        path="/zone-intervention"
        description={`Acanthe Jardins intervient dans un rayon de ${radius} km autour de Saint-Victoret : métropole Aix-Marseille, étang de Berre, Var et Vaucluse. Découvrez votre ville.`}
        jsonLd={localBusinessLd()} />

      {/* Header */}
      <section className="pt-32 md:pt-40 pb-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-3xl">
            <Overline>Où nous intervenons</Overline>
            <h1 className="mt-4 font-display text-5xl lg:text-6xl text-forest leading-[1.02]">Votre paysagiste, près de chez vous</h1>
            <p className="mt-5 text-lg text-stone leading-relaxed">{zone?.intro || "Basés à Saint-Victoret, nous intervenons sur toute la métropole Aix-Marseille-Provence et au-delà."}</p>
          </Reveal>
        </div>
      </section>

      {/* Map + stats */}
      <section className="pb-14">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-[1.6fr_1fr] gap-6 items-stretch">
          <Reveal className="rounded-[26px] overflow-hidden border border-border shadow-sm bg-white min-h-[380px] lg:min-h-[500px]">
            <ZoneMap center={zone?.center} radiusKm={radius} cities={cities} className="h-full w-full" />
          </Reveal>
          <Reveal delay={0.1} className="flex flex-col gap-4">
            {[
              { icon: Compass, label: "Rayon d'action", value: `${radius} km`, sub: "autour de Saint-Victoret" },
              { icon: Building2, label: "Communes desservies", value: `${pages.length || cities.length || "—"}`, sub: "pages locales dédiées" },
              { icon: MapPin, label: "Départements", value: "13 · 83 · 84", sub: "Bouches-du-Rhône, Var, Vaucluse" },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl bg-white border border-border p-6 flex items-center gap-4" data-testid={`zone-stat-${s.label}`}>
                <div className="h-12 w-12 rounded-xl bg-brand/10 flex items-center justify-center shrink-0"><s.icon className="text-brand" size={24} /></div>
                <div>
                  <p className="font-display text-3xl text-forest leading-none">{s.value}</p>
                  <p className="text-sm font-medium text-forest mt-1">{s.label}</p>
                  <p className="text-xs text-stone">{s.sub}</p>
                </div>
              </div>
            ))}
            <div className="rounded-2xl bg-forest text-cream p-6 mt-auto">
              <p className="font-display text-xl">Votre commune n'est pas listée ?</p>
              <p className="text-cream/70 text-sm mt-1">Nous étudions chaque projet. Contactez-nous pour vérifier notre disponibilité.</p>
              <Link to="/contact" className="mt-4 inline-flex items-center gap-2 rounded-full bg-flora text-forest px-5 py-2.5 text-sm font-semibold hover:bg-cream transition-colors"><Phone size={15} /> Demander un devis</Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Local SEO pages */}
      {pages.length > 0 && (
        <section className="py-14 bg-[#F0EFEA]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal className="mb-8">
              <Overline>Paysagiste par ville</Overline>
              <h2 className="mt-3 font-display text-4xl text-forest">Nos pages locales</h2>
              <p className="mt-2 text-stone max-w-2xl">Entretien, création, élagage, terrassement… Découvrez notre savoir-faire au plus près de chez vous.</p>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="zone-pages">
              {pages.map((c, i) => (
                <Reveal key={c.slug} delay={(i % 3) * 0.06}>
                  <Link to={`/conseils/${c.slug}`} data-testid={`zone-page-${c.slug}`}
                    className="group flex flex-col h-full rounded-[20px] overflow-hidden bg-white border border-border hover:-translate-y-1.5 hover:shadow-xl hover:shadow-forest/5 transition-all">
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img src={c.cover_image ? fileUrl(c.cover_image) : ART_FALLBACK} alt={c.title} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700" />
                      <span className="absolute top-3 left-3 rounded-full bg-cream/90 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-brand inline-flex items-center gap-1"><MapPin size={11} />{c.ville} {c.code_postal}</span>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="font-display text-xl text-forest leading-tight">{c.title}</h3>
                      {c.excerpt && <p className="text-sm text-stone mt-2 line-clamp-2 flex-1">{c.excerpt}</p>}
                      <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand group-hover:gap-2 transition-all">Découvrir <ArrowUpRight size={15} /></span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-2xl mx-auto px-6">
          <div className="rounded-2xl bg-white border border-border p-8 shadow-sm">
            <h3 className="font-display text-3xl text-forest mb-1 text-center">Un projet dans votre secteur ?</h3>
            <p className="text-stone text-center mb-6">Un expert vous recontacte sous 24h — l'étude est gratuite.</p>
            <CallbackForm compact />
          </div>
        </div>
      </section>
    </div>
  );
}
